import React, {useState, useEffect, useRef, useMemo} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    getOutcomesForContactOptions,
    getInteractionById,
    updateInteraction, saveInteractionFiles, getInteractionFileMetadata,
} from '../services/interaction-service';
import Interaction from '../models/Interaction';
import { ContactOption } from '../models/enums/ContactOption';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ErrorToast from '../components/ErrorToast';
import FileMetadata from "../models/FileMetadata";
import {getAINotesSummary} from "../services/ai-service";
import {getDateFromString, getDefaultTimeZone, getTimeZones} from "../utils/DateTimeUtils";

const EditInteractionPage: React.FC = () => {
    type TagOption = { label: string; value: string };

    const contactOptions = Object.values(ContactOption).map(option => ({
        value: option,
        label: option,
    }));

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [loadedInteraction, setLoadedInteraction] = useState<Interaction | null>(null);
    const [form, setForm] = useState({
        subject: '',
        interactionDate: '',
        interactionTime: '',
        timeZone: getDefaultTimeZone(),
        type: contactOptions[0]?.value || Object.values(ContactOption)[0],
        outcome: '',
        duration: undefined as number | undefined,
        durationUnit: '' as 'Hours' | 'Minutes' | '',
        notes: '',
        tags: [] as MultiValue<TagOption>,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loadingInitial, setLoadingInitial] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errorToast, setErrorToast] = useState('');
    const { subscriptionPlan, user } = useAuth();

    const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
    const [outcomesMap, setOutcomesMap] = useState<Map<ContactOption, string[]>>(new Map());
    const [outcomeOptions, setOutcomeOptions] = useState<TagOption[]>([]);
    const isFirstPageLoad = useRef(true);

    const [fileError, setFileError] = useState<string | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [fileMetaData, setFileMetadata] = useState<FileMetadata[] | null>(null);

    const [aiSummary, setAiSummary] = useState<string>('');
    const [aiLoading, setAiLoading] = useState(false);

    const MAX_FILE_BYTES = 10 * 1024 * 1024;
    const MAX_TOTAL_BYTES = 25 * 1024 * 1024;

    const acceptedFileTypes = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.webp";

    const disableAIAccess = useMemo(() => {
        return !subscriptionPlan?.aiAccess;
    }, [subscriptionPlan]);

    const handleGenerateSummary = async () => {
        if (disableAIAccess || !form.notes.trim() || aiLoading) return;
        try {
            if (user?.id) {
                setAiLoading(true);
                const response = await getAINotesSummary(user?.id, form.notes);
                if (response.status === 200 && response.data) {
                    setAiSummary(response.data);
                } else {
                    throw new Error('Error retrieving AI summary ' + response);
                }
            }
        } catch (e) {
            console.error('AI summary error', e);
            setErrorToast('Unable to generate summary at this time. Please try again later');
        } finally {
            setAiLoading(false);
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setFileError(null);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileError(null);
        const picks = Array.from(e.target.files ?? []);
        if (!picks.length) return;

        const accepted = [...files];
        let total = accepted.reduce((s, f) => s + f.size, 0);

        const seen = new Set(accepted.map((f: File) => f.name));
        const rejected: string[] = [];
        for (const uploadedFile of picks) {
            if (seen.has(uploadedFile.name)) continue;
            if(fileMetaData?.map((file: FileMetadata) => file.fileName).includes(uploadedFile.name)) {
                rejected.push(`${uploadedFile.name} (a file with this name has already been uploaded for this interaction`);
                continue;
            }
            if (uploadedFile.size > MAX_FILE_BYTES) {
                rejected.push(`${uploadedFile.name} (>${Math.round(MAX_FILE_BYTES/1024/1024)}MB)`);
                continue;
            }
            if (total + uploadedFile.size > MAX_TOTAL_BYTES) {
                rejected.push(`${uploadedFile.name} (exceeds total ${Math.round(MAX_TOTAL_BYTES/1024/1024)}MB)`);
                continue;
            }
            accepted.push(uploadedFile);
            seen.add(uploadedFile.name);
            total += uploadedFile.size;
        }

        setFiles(accepted);
        if (rejected.length) setFileError(`Skipped: ${rejected.join(", ")}`);
        e.target.value = "";
    };

    const disableFileUploads = useMemo(() => {
        return !subscriptionPlan?.fileUploads
    }, [subscriptionPlan]);

    const capitalizeFirst = (str?: string): string => {
        if (!str) return '';
        const lower = str.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    };

    useEffect(() => {
        const fetchFileMetadata = async () => {
            try {
                if (loadedInteraction?.id && loadedInteraction.hasFiles) {
                    const response = await getInteractionFileMetadata(loadedInteraction.id);
                    if (response.status === 200 && response.data) {
                        setFileMetadata(response.data);
                    } else {
                        throw new Error('Unable to retrieve file metadata: ' + response.status);
                    }
                } else setFileMetadata([]);

            } catch (err: any) {
                console.error('Error fetching file metadata', err);
                setErrorToast('Unable to load files for interaction at this time.')
            }
        }
        if (loadedInteraction) {
            fetchFileMetadata();
        }
    }, [loadedInteraction]);

    useEffect(() => {
        const loadOutcomes = async () => {
            try {
                const response = await getOutcomesForContactOptions();
                if (response.data) setOutcomesMap(response.data);
            } catch (err: any) {
                console.error('Failed to fetch outcomes map', err);
            }
        };
        if (isFirstPageLoad.current) {
            isFirstPageLoad.current = false;
            loadOutcomes();
        }
    }, []);

    useEffect(() => {
        const loadInteraction = async () => {
            if (!id) {
                setErrorToast('No interaction ID provided.');
                setLoadingInitial(false);
                return;
            }
            try {
                const response = await getInteractionById(Number(id));
                if (response.status === 200 && response.data) {
                    const data: Interaction = response.data;
                    setLoadedInteraction(data);
                    setForm({
                        subject: data.subject,
                        interactionDate: data.interactionDate.toISOString().split('T')[0],
                        interactionTime: data.interactionTime || '',
                        timeZone: data.timeZone || getDefaultTimeZone(),
                        type: data.type,
                        outcome: data.outcome,
                        duration: data.duration,
                        durationUnit: data.durationUnit
                            ? (capitalizeFirst(data.durationUnit) as 'Hours' | 'Minutes')
                            : '',
                        notes: data.notes || '',
                        tags: data.tags? data.tags.map(tag => ({ label: tag, value: tag })) : [],
                    });
                    setTagOptions(data.tags? data.tags.map(tag => ({ label: tag, value: tag })) : []);
                    if(!disableAIAccess && data.aiNotesSummary) setAiSummary(data.aiNotesSummary);
                } else {
                    throw new Error('Invalid response loading interaction');
                }
            } catch (err: any) {
                console.error('Error loading interaction', err);
                setErrorToast('Error loading interaction. Please try again later.');
            } finally {
                setLoadingInitial(false);
            }
        };
        loadInteraction();
    }, [id]);

    useEffect(() => {
        if (outcomesMap.size) {
            const outcomesForType = outcomesMap.get(form.type) || [];
            setOutcomeOptions(outcomesForType.map(o => ({ label: o, value: o })));
        }
    }, [form.type, outcomesMap]);

    const validate = (tags: string[]): boolean => {
        const errs: Record<string, string> = {};
        if (!form.subject.trim()) errs.subject = 'Subject is required';
        if (!form.interactionDate) errs.interactionDate = 'Interaction date is required';
        if (!form.outcome) errs.outcome = 'Outcome is required';
        if (form.interactionTime && !form.timeZone) errs.timeZone = 'Time zone is required when a time is set';
        if (form.duration && !form.durationUnit) errs.duration = 'Duration unit is required';

        const totalTagChars = tags.reduce((sum, t) => sum + t.length, 0);
        if (totalTagChars > 255)
            errs.tags = `Total tags must be 255 characters or fewer (you have ${totalTagChars})`;

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        setLoading(true);
        const mappedTags = form.tags.map(t => t.value);
        if (!loadedInteraction || !validate(mappedTags)) {
            setLoading(false);
            return
        }

        const updated: Interaction = {
            ...loadedInteraction,
            subject: form.subject,
            interactionDate: getDateFromString(form.interactionDate),
            interactionTime: form.interactionTime,
            timeZone: form.interactionTime? form.timeZone : '',
            type: form.type,
            outcome: form.outcome,
            duration: form.duration,
            durationUnit: form.durationUnit ? form.durationUnit.toUpperCase() : undefined,
            notes: form.notes,
            tags: mappedTags,
            aiNotesSummary: aiSummary
        };

        try {
            const response = await updateInteraction(updated);
            if (response.status === 204) {
                let errorMessage;
                if (files.length) {
                    const formData = new FormData();
                    files.forEach(f => formData.append("files", f));

                    const uploadResponse = await saveInteractionFiles(Number(id), formData);
                    if (uploadResponse.status === 400) {
                        errorMessage = 'Your interaction was saved successfully but we were unable to upload all files due ' +
                            'to size or invalid file type. You can try again from the edit interaction page.';
                    } else if (uploadResponse.status === 207) {
                        errorMessage = 'Your interaction was saved successfully but we were unable to upload all files. ' +
                            'You can try again from the edit interaction page.';
                    } else if (uploadResponse.status === 500) {
                        errorMessage = 'Your interaction was saved successfully but we were unable to upload the files. ' +
                            'You can try again from the edit interaction page.';
                    }
                }
                navigate(`/interaction/${id}`, {
                    state: {
                        errorMessage: errorMessage
                    }
                });
            } else throw new Error('Failed to update');
        } catch (err: any) {
            console.error('Error updating interaction', err);
            setErrorToast('Unable to update interaction at this time. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    if (loadingInitial) {
        return (
            <div data-testid="loading-placeholder" className="min-h-screen flex flex-col">
                <NavBar />
                <main className="flex-grow container mx-auto px-4 py-6 text-center">Loading...</main>
                <Footer />
                <ErrorToast
                    data-testid="error-toast"
                    message={errorToast}
                    clearError={() => setErrorToast('')}
                />
            </div>
        );
    }

    const timeZones = getTimeZones();
    return (
        <div data-testid="interaction-container">
            <NavBar />
            <div className="max-w-3xl mx-auto p-6">
                <button
                    data-testid="back-btn"
                    onClick={() => navigate(`/interaction/${id}`)}
                    className="text-crumbblue hover:underline mb-4"
                >
                    &larr; Back to Interaction
                </button>

                <div className="max-w-3xl mx-auto p-6 bg-white rounded shadow">
                    <h1 className="text-2xl font-semibold mb-4">Edit Interaction</h1>
                    <div className="space-y-4">
                        {/* Subject */}
                        <div>
                            <label htmlFor="subject" className="block font-medium">Subject *</label>
                            <input
                                id="subject"
                                type="text"
                                maxLength={100}
                                value={form.subject}
                                onChange={e => setForm({ ...form, subject: e.target.value })}
                                className="w-full border rounded p-2"
                            />
                            <p className="text-gray-500 text-sm">{form.subject.length}/100</p>
                            {errors.subject && (
                                <p data-testid="subject-error" className="text-red-600 text-sm">
                                    {errors.subject}
                                </p>
                            )}
                        </div>

                        {/* Date */}
                        <div>
                            <label htmlFor="interaction-date" className="block font-medium">Interaction Date *</label>
                            <input
                                id="interaction-date"
                                type="date"
                                value={form.interactionDate}
                                onChange={e => setForm({ ...form, interactionDate: e.target.value })}
                                className="w-full border rounded p-2"
                            />
                            {errors.interactionDate && (
                                <p data-testid="date-error" className="text-red-600 text-sm">
                                    {errors.interactionDate}
                                </p>
                            )}
                        </div>

                        {/* Time + Time Zone (optional) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label htmlFor="interaction-time" className="block font-medium">Time</label>
                                <input
                                    id="interaction-time"
                                    data-testid="interaction-time"
                                    type="time"
                                    value={form.interactionTime}
                                    onChange={e => setForm({...form, interactionTime: e.target.value})}
                                    className="w-full border rounded p-2"
                                />
                            </div>
                            <div>
                                <label htmlFor="interaction-tz" className="block font-medium">Time Zone</label>
                                <select
                                    id="interaction-tz"
                                    data-testid="interaction-tz"
                                    value={form.timeZone}
                                    onChange={e => setForm({...form, timeZone: e.target.value})}
                                    className="w-full border rounded p-2"
                                >
                                    {timeZones.map(tz => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                                {errors.timeZone && <p className="text-red-600 text-sm">{errors.timeZone}</p>}
                            </div>
                        </div>

                        {/* Type */}
                        <div>
                            <label htmlFor="type" className="block font-medium">Interaction Type *</label>
                            <select
                                id="type"
                                value={form.type}
                                onChange={e => setForm({ ...form, type: e.target.value as ContactOption, outcome: '' })}
                                className="w-full border rounded p-2"
                            >
                                {contactOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Outcome */}
                        <div>
                            <label htmlFor="outcome" className="block font-medium">Outcome *</label>
                            <select
                                id="outcome"
                                data-testid="outcome-select"
                                value={form.outcome}
                                onChange={e => setForm({ ...form, outcome: e.target.value })}
                                className="w-full border rounded p-2"
                            >
                                <option value="">Select outcome</option>
                                {outcomeOptions.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                            {errors.outcome && (
                                <p data-testid="outcome-error" className="text-red-600 text-sm">{errors.outcome}</p>
                            )}
                        </div>

                        {/* Duration & Unit */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="duration" className="block font-medium">Duration</label>
                                <input
                                    id="duration"
                                    type="number"
                                    value={form.duration ?? ''}
                                    onChange={e => setForm({ ...form, duration: Number(e.target.value) })}
                                    className="w-full border rounded p-2"
                                />
                            </div>
                            <div>
                                <label htmlFor="duration-unit" className="block font-medium">Time Unit</label>
                                <select
                                    id="duration-unit"
                                    value={form.durationUnit}
                                    onChange={e => setForm({ ...form, durationUnit: e.target.value as 'Hours' | 'Minutes' })}
                                    className="w-full border rounded p-2"
                                >
                                    <option value="">Select unit</option>
                                    <option value="Hours">Hours</option>
                                    <option value="Minutes">Minutes</option>
                                </select>
                                {errors.duration && <p data-testid="duration-error" className="text-red-600 text-sm">{errors.duration}</p>}
                            </div>
                        </div>

                        {/* Notes */}
                        <div data-testid="notes-container">
                            <label htmlFor="notes" className="block font-medium">Notes</label>
                            <textarea
                                id="notes"
                                rows={8}
                                maxLength={10000}
                                value={form.notes}
                                onChange={e => setForm({ ...form, notes: e.target.value })}
                                className="w-full border rounded p-2"
                            />
                            <p className="text-gray-500 text-sm mt-1">{form.notes.length}/10000 characters</p>
                            {form.notes.length > 9000 && (
                                <p className="text-yellow-600 text-sm">You may want to consider uploading a file for longer notes.</p>
                            )}
                        </div>

                        {/* AI Summary */}
                        {disableAIAccess && disableFileUploads && (
                            <div className="mb-3 text-sm text-gray-600" data-testid="features-disabled-message">
                                Some features have been disabled under your Free plan.
                                <span className="ml-1">Upgrade your plan to use them.</span>
                            </div>
                        )}
                        <div className="mt-3 space-y-2" data-testid="ai-summary-section">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid="ai-generate-btn"
                                    onClick={handleGenerateSummary}
                                    disabled={disableAIAccess || !form.notes.trim() || aiLoading || !!aiSummary}
                                    className={`group inline-flex items-center gap-2 px-3 py-1 rounded border transition-all duration-200
                                        ${disableAIAccess || !form.notes.trim() || aiLoading || !!aiSummary
                                        ? 'cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500'
                                        : 'border-crumbblue bg-crumbblue text-white hover:-translate-y-0.5 hover:shadow-md ' +
                                        'active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-crumbblue/40'}
                                            ${aiLoading ? 'animate-pulse' : ''}`}
                                >
                                    {aiLoading && (
                                        <svg
                                            className="h-4 w-4 animate-spin text-white"
                                            viewBox="0 0 24 24"
                                            aria-hidden="true"
                                            data-testid="loading-graphic"
                                        >
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4A4 4 0 0 0 8 12H4z"/>
                                        </svg>
                                    )}
                                    {aiLoading ? 'Generating...' : 'Generate AI Summary'}
                                </button>

                                {aiSummary && (
                                    <button
                                        type="button"
                                        data-testid="ai-clear-btn"
                                        onClick={() => setAiSummary('')}
                                        className="px-3 py-1 rounded border hover:bg-gray-50"
                                    >
                                        Clear Summary
                                    </button>
                                )}
                            </div>

                            {aiSummary && (
                                <div
                                    data-testid="ai-summary"
                                    className="border rounded p-3 bg-gray-50 whitespace-pre-wrap"
                                >
                                    {aiSummary}
                                </div>
                            )}
                        </div>

                        {/* Attachments */}
                        <div>
                            <label className="block font-medium">Attachments</label>
                            <input
                                type="file"
                                disabled={disableFileUploads}
                                className="w-full border rounded p-2"
                                onChange={handleFileChange}
                                accept={acceptedFileTypes}
                                multiple
                                data-testid="file-input"
                            />
                            {fileError && <p data-testid="file-error" className="text-red-600 text-sm mt-1">{fileError}</p>}
                            {!!files.length && (
                                <ul data-testid="file-summaries" className="text-sm mt-2 space-y-1">
                                    {files.map((f, idx) => (
                                        <li key={`${f.name}-${f.lastModified}`} className="flex items-center justify-between">
                                            <span className="truncate mr-3">
                                                {f.name} — {(f.size/1024/1024).toFixed(2)} MB
                                             </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(idx)}
                                                className="px-2 py-1 rounded border text-xs hover:bg-gray-50"
                                                aria-label={`Remove ${f.name}`}
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Tags */}
                        <div data-testid="tags-container">
                            <label className="block font-medium">Tags</label>
                            <CreatableSelect<TagOption, true>
                                options={tagOptions}
                                isMulti
                                value={form.tags}
                                onChange={vals => setForm({ ...form, tags: vals })}
                                onCreateOption={inputValue => {
                                    const newOpt = { label: inputValue, value: inputValue };
                                    setTagOptions(prev => [...prev, newOpt]);
                                    setForm(prev => ({ ...prev, tags: [...prev.tags, newOpt] }));
                                }}
                                placeholder="Select or type to create tags"
                            />
                            {errors.tags && (
                                <p data-testid="tags-error" className="tex-red-600 text-sm">{errors.tags}</p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-between mt-6">
                            <button
                                data-testid="cancel-btn"
                                disabled={loading}
                                onClick={() => navigate(`/interaction/${id}`)}
                                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="submit-btn"
                                onClick={handleSubmit}
                                className="px-4 py-2 rounded bg-crumbblue text-white"
                                disabled={loading}
                            >
                                {loading ? 'Updating...' : 'Update Interaction'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            <ErrorToast
                data-testid="error-toast"
                message={errorToast}
                clearError={() => setErrorToast('')}
            />
        </div>
    );
};

export default EditInteractionPage;
